import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { Observable, switchMap } from 'rxjs';
import { ScreenLockApiService } from '../../../core/services/screen-lock-api.service';
import { ScreenLockTokenService } from '../../../core/services/screen-lock-token.service';
import { IScreenLockTarget, IScreenLockVerifyResult } from '../../../core/services/screen-lock.interface';
import { ScreenLockService } from '../../../core/services/screen-lock.service';
import { ELoadStatus } from '../../../state/state.interface';
import { ESnackbar, SnackbarService } from '../../../utilities/snackbar.service';

export interface IScreenLockComponentState {
  isWrongPin: boolean;
  verifyStatus: ELoadStatus;
  /** Screen being verified. Null on the overlay (follows `ScreenLockService`); set by the settings dialog, where no screen runs. */
  target: IScreenLockTarget | null;
}

/**
 * Keeps the verify request out of the component: `AI_RULES` [http] requires component → store → service.
 * Provided by `ScreenLockComponent`, so its lifetime matches the overlay.
 */
@Injectable()
export class ScreenLockStore extends ComponentStore<IScreenLockComponentState> {
  private readonly api = inject(ScreenLockApiService);
  private readonly screenLockService = inject(ScreenLockService);
  private readonly snackbar = inject(SnackbarService);
  private readonly tokenService = inject(ScreenLockTokenService);
  private readonly translate = inject(TranslateService);

  public readonly isWrongPin: Signal<boolean> = this.selectSignal(
    (state: IScreenLockComponentState) => state.isWrongPin,
  );
  public readonly isVerifying: Signal<boolean> = this.selectSignal(
    (state: IScreenLockComponentState) => state.verifyStatus === ELoadStatus.loading,
  );
  /** The PIN was accepted. Used by the settings dialog, which has no overlay to react to. */
  public readonly isVerified: Signal<boolean> = this.selectSignal(
    (state: IScreenLockComponentState) => state.verifyStatus === ELoadStatus.success && !state.isWrongPin,
  );

  public readonly verifyPin = this.effect((pin$: Observable<string>) =>
    pin$.pipe(
      switchMap((pin: string) => {
        const target: IScreenLockTarget | null = this.get().target ?? this.screenLockService.target();

        if (!target) {
          return [];
        }

        this.patchState({ isWrongPin: false, verifyStatus: ELoadStatus.loading });

        return this.api.verifyPin(target.entityType, target.entityId, pin).pipe(
          tapResponse(
            ({ data }) => {
              this.patchState({ isWrongPin: !data.isValid, verifyStatus: ELoadStatus.success });

              if (data.isValid) {
                this.acceptUnlock(target, data);
              }
            },
            // An HTTP failure is not a wrong PIN: leave isWrongPin false so the generic interceptor owns the
            // feedback (401 → re-auth, else snackbar). The screen stays locked either way. See SCREEN_LOCK_CONTEXT.md.
            (error: unknown) => {
              this.patchState({ isWrongPin: false, verifyStatus: ELoadStatus.failure });

              // The timeout is applied after the interceptor, so a hung request never reaches its snackbar.
              // Without this the keypad would just quietly become usable again with nothing said.
              if (!(error instanceof HttpErrorResponse)) {
                this.snackbar.open(this.translate.instant('apiError.error'), ESnackbar.error);
              }
            },
          ),
        );
      }),
    ),
  );

  constructor() {
    super({ isWrongPin: false, target: null, verifyStatus: ELoadStatus.initial });
  }

  /**
   * Also resets `verifyStatus`: clearing only `isWrongPin` left the status at `success`, which made
   * {@link isVerified} report a verified screen after a *rejected* PIN. See SCREEN_LOCK_CONTEXT.md.
   */
  public clearError(): void {
    this.patchState({ isWrongPin: false, verifyStatus: ELoadStatus.initial });
  }

  /** Points the store at a screen that is not the one `ScreenLockService` is guarding. */
  public setTarget(target: IScreenLockTarget): void {
    this.patchState({ target });
  }

  /**
   * Records the unlock token, then lifts the overlay only for the screen the lock is actually running on —
   * the settings dialog verifies a board nobody is standing in front of. See SCREEN_LOCK_CONTEXT.md.
   */
  private acceptUnlock(target: IScreenLockTarget, result: IScreenLockVerifyResult): void {
    if (result.unlockToken && result.expiresInSeconds) {
      this.tokenService.store(target, result.unlockToken, result.expiresInSeconds);
    }

    const runningTarget: IScreenLockTarget | null = this.screenLockService.target();

    if (runningTarget && runningTarget.entityType === target.entityType && runningTarget.entityId === target.entityId) {
      this.screenLockService.unlock();
    }
  }
}
